/**
 * 学员端数据钩子（TanStack Query）
 * -----------------------------------------------------------------------------
 * 服务端数据一律走这里；只有 UI 状态（当前步骤、本地草稿缓存）用 zustand。
 *
 * 会话查询 `retry: false`：401 是「未授权」这个明确结论，重试只会拖慢跳转。
 */
import { type UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { consumeEntryFromUrl } from './client';
import {
  fetchInbox,
  fetchInboxUnreadCount,
  fetchSupportQrcode,
  getForm,
  getMyInvoice,
  getProfileBasic,
  getRegistration,
  getSession,
  kycSteps,
  listEnrollOptions,
  listEntrances,
  listMyRegistrations,
  markInboxRead,
  type SupportQrcodeRegion,
} from './h5';
import type {
  EnrollOptionView,
  FormSchemaView,
  H5EntranceView,
  H5Profile,
  H5ProfileBasic,
  InboxPageView,
  InvoiceRecordView,
  KycStepsView,
  RegistrationBriefView,
  RegistrationDetailView,
  SupportQrcodeView,
} from './types';

export const h5QueryKeys = {
  session: ['h5', 'session'] as const,
  /** 本人账号资料（含手机号明文，仅预填用） */
  profileBasic: ['h5', 'profileBasic'] as const,
  entrances: ['h5', 'entrances'] as const,
  enrollOptions: ['h5', 'enrollOptions'] as const,
  registration: (id: string) => ['h5', 'registration', id] as const,
  form: (enrollTypeId: string) => ['h5', 'form', enrollTypeId] as const,
  myRegistrations: ['h5', 'registrations', 'mine'] as const,
  inbox: (page: number) => ['h5', 'inbox', page] as const,
  inboxUnread: ['h5', 'inbox', 'unread'] as const,
  invoice: (registrationId: string) => ['h5', 'invoice', registrationId] as const,
  kycSteps: (registrationId: string) => ['h5', 'kycSteps', registrationId] as const,
  /** 三个区域码摊平成元组，避免把对象放进 key（对象每次渲染都是新引用） */
  supportQrcode: (region?: SupportQrcodeRegion) =>
    [
      'h5',
      'supportQrcode',
      region?.provinceCode ?? '',
      region?.cityCode ?? '',
      region?.districtCode ?? '',
    ] as const,
};

export function useSession(): UseQueryResult<H5Profile> {
  return useQuery({
    queryKey: h5QueryKeys.session,
    queryFn: getSession,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useEntrances(): UseQueryResult<{ items: H5EntranceView[] }> {
  return useQuery({
    queryKey: h5QueryKeys.entrances,
    queryFn: listEntrances,
    retry: false,
    staleTime: 60 * 1000,
  });
}

/**
 * 可报「报名类型 × 批次」组合（报名办理页）。
 * `retry: false`：401 是会话结论；空列表是「无可报批次」的正常业务分支。
 */
export function useEnrollOptions(): UseQueryResult<{ items: EnrollOptionView[] }> {
  return useQuery({
    queryKey: h5QueryKeys.enrollOptions,
    queryFn: listEnrollOptions,
    retry: false,
    staleTime: 60 * 1000,
  });
}

export function useRegistration(id: string | undefined): UseQueryResult<RegistrationDetailView> {
  return useQuery({
    queryKey: h5QueryKeys.registration(id ?? ''),
    queryFn: () => getRegistration(id as string),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useFormSchema(enrollTypeId: string | undefined): UseQueryResult<FormSchemaView> {
  return useQuery({
    queryKey: h5QueryKeys.form(enrollTypeId ?? ''),
    queryFn: () => getForm(enrollTypeId as string),
    enabled: Boolean(enrollTypeId),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * 落地即记住入口码：`?entry=xxx` 会被写入会话存储，
 * 后续所有 `/api/h5/*` 请求都会带上 `x-entry` 头（归因 + 入口级配置）。
 */
/**
 * 核验链的当前状态（含证件照链接）。
 *
 * 用在**报名确认页**：职业证件照不是表单字段（它由 ③ 生成、挂在 `Asset` 上），
 * 所以表单答案里根本没有它，只能走这个读接口取。`retry: false`：确认页拿它只为
 * 展示一张缩略图，取不到就退回文字占位，不该为它反复重试、更不该挡住提交。
 */
export function useKycSteps(
  registrationId: string | undefined,
  enabled = true,
): UseQueryResult<KycStepsView> {
  return useQuery({
    queryKey: h5QueryKeys.kycSteps(registrationId ?? ''),
    queryFn: () => kycSteps(registrationId as string),
    enabled: Boolean(registrationId) && enabled,
    retry: false,
    // 签名链接有有效期，缓存太久反而会拿到过期 URL
    staleTime: 60 * 1000,
  });
}

export function useEntryCapture(): void {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  useEffect(() => {
    consumeEntryFromUrl(search);
  }, [search]);
}

// ---------------------------------------------------------------- 服务台状态区 / 消息中心

export function useMyRegistrations(): UseQueryResult<{
  items: RegistrationBriefView[];
  total: number;
}> {
  return useQuery({
    queryKey: h5QueryKeys.myRegistrations,
    queryFn: () => listMyRegistrations(1, 20),
    retry: false,
    staleTime: 30 * 1000,
  });
}

/**
 * 本人账号资料（姓名 + 手机号明文）。
 *
 * 只在「填写页回显」「报名表单预填」这类**需要写进表单值**的地方用；
 * 纯展示一律读 `useSession().phoneMasked`，避免明文在页面间多传一手。
 */
export function useProfileBasic(
  options: { enabled?: boolean } = {},
): UseQueryResult<H5ProfileBasic> {
  return useQuery({
    queryKey: h5QueryKeys.profileBasic,
    queryFn: getProfileBasic,
    retry: false,
    staleTime: 30 * 1000,
    enabled: options.enabled ?? true,
  });
}

export function useInbox(page = 1): UseQueryResult<InboxPageView> {
  return useQuery({
    queryKey: h5QueryKeys.inbox(page),
    queryFn: () => fetchInbox(page),
    retry: false,
  });
}

/** 未读数（服务台首页角标 / 底部导航红点共用，读缓存不重复请求） */
export function useInboxUnreadCount(): UseQueryResult<number> {
  return useQuery({
    queryKey: h5QueryKeys.inboxUnread,
    queryFn: fetchInboxUnreadCount,
    retry: false,
    staleTime: 30 * 1000,
  });
}

/** 标记已读后失效消息相关缓存（列表 + 未读角标一起刷新） */
export function useMarkInboxRead(): (id: string) => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    async (id: string) => {
      await markInboxRead(id);
      void queryClient.invalidateQueries({ queryKey: ['h5', 'inbox'] });
    },
    [queryClient],
  );
}

// ---------------------------------------------------------------- 客服码

/**
 * 客服码（联系客服页）。
 *
 * `retry: false` —— `SUPPORT-004`（404 无可用码）是**正常业务分支**，
 * 重试只会让页面多转两圈再给同一结论；调用方按「取不到即展示文字兜底」处理。
 */
export function useSupportQrcode(region?: SupportQrcodeRegion): UseQueryResult<SupportQrcodeView> {
  return useQuery({
    queryKey: h5QueryKeys.supportQrcode(region),
    queryFn: () => fetchSupportQrcode(region),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------- 发票申请（C-25）

/**
 * 本单当前开票记录（`null` = 从未申请）。
 *
 * `retry: false`：403（他人报名单 / 不存在统一 `AUTHZ-003`）与 404 都是确定结论，
 * 重试没有意义。`staleTime` 取 0（默认）：开票状态会被财务在后台改动，
 * 回到详情页必须重新拉，不能拿缓存里的旧状态给学员看。
 */
export function useMyInvoice(
  registrationId: string | undefined,
): UseQueryResult<InvoiceRecordView | null> {
  return useQuery({
    queryKey: h5QueryKeys.invoice(registrationId ?? ''),
    queryFn: () => getMyInvoice(registrationId as string),
    enabled: Boolean(registrationId),
    retry: false,
  });
}
